import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `Política de Privacidad

Última actualización: abril 2026

En Inflamación Zero 48h, respetamos y protegemos la privacidad de nuestros usuarios. Esta política describe cómo recopilamos, usamos y protegemos su información personal.

Información que recopilamos:
• Información proporcionada voluntariamente al realizar una compra (nombre, correo electrónico, información de pago).
• Datos de uso del sitio web recopilados automáticamente (dirección IP, tipo de navegador, páginas visitadas).

Uso de la información:
• Procesar su compra y entregar el producto digital adquirido.
• Enviar comunicaciones relacionadas con su compra.
• Mejorar nuestro sitio web y la experiencia del usuario.

Protección de datos:
• Utilizamos medidas de seguridad estándar de la industria para proteger su información personal.
• No vendemos, intercambiamos ni transferimos su información personal a terceros sin su consentimiento.
• La información de pago es procesada de forma segura a través de proveedores de pago certificados.

Cookies:
• Nuestro sitio web puede utilizar cookies para mejorar la experiencia del usuario.
• Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.

Sus derechos:
• Puede solicitar acceso, corrección o eliminación de sus datos personales en cualquier momento.
• Puede optar por no recibir comunicaciones de marketing.

Cambios a esta política:
• Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.`;
const termsContent = `Términos y Condiciones

Última actualización: abril 2026

Al acceder y utilizar este sitio web y adquirir nuestros productos digitales, usted acepta los siguientes términos y condiciones.

Producto digital:
• Inflamación Zero 48h es una guía digital educativa e informativa.
• El contenido no constituye consejo médico profesional ni sustituye la consulta con un profesional de la salud.
• Los resultados pueden variar de persona a persona.

Compra y acceso:
• Al completar su compra, recibirá acceso instantáneo al producto digital.
• El acceso es personal e intransferible.
• No está permitido distribuir, copiar o compartir el contenido del producto.

Política de reembolso:
• Debido a la naturaleza digital del producto, todas las ventas son finales.
• En casos excepcionales, puede contactarnos para evaluar su situación.

Descargo de responsabilidad:
• La información proporcionada es solo para fines educativos e informativos.
• No garantizamos resultados específicos.
• Siempre consulte con su médico antes de realizar cambios en su dieta o estilo de vida.
• No somos responsables por el uso indebido de la información proporcionada.

Propiedad intelectual:
• Todo el contenido, incluyendo textos, imágenes y diseños, es propiedad de Inflamación Zero 48h.
• Queda prohibida la reproducción total o parcial sin autorización previa.

Modificaciones:
• Nos reservamos el derecho de modificar estos términos en cualquier momento.
• El uso continuado del sitio después de los cambios constituye la aceptación de los nuevos términos.`;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-gray-50 py-12 border-t border-gray-200" data-id="element-36">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-37">
          <p className="text-sm text-gray-500 mb-6 max-w-3xl mx-auto leading-relaxed" data-id="element-38">
            Descargo de responsabilidad: La información proporcionada en este
            sitio web y en la guía digital es solo para fines educativos e
            informativos. No pretende sustituir el consejo, diagnóstico o
            tratamiento médico profesional. Siempre busque el consejo de su
            médico u otro proveedor de salud calificado con cualquier pregunta
            que pueda tener sobre una condición médica.
          </p>

          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-39">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-500 hover:text-brand-teal underline underline-offset-2 transition-colors" data-id="element-40">
              Política de Privacidad
            </button>
            <span className="text-gray-300" data-id="element-41">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-500 hover:text-brand-teal underline underline-offset-2 transition-colors" data-id="element-42">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-sm text-gray-400" data-id="element-43">
            &copy; {new Date().getFullYear()} Inflamación Zero 48h. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-44">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-45">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-46">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-47">
                <h3 className="text-xl font-bold text-brand-dark" data-id="element-48">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-49">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-50" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-51">
                <pre className="whitespace-pre-wrap text-sm text-gray-600 leading-relaxed font-sans" data-id="element-52">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </pre>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}